// Aging (Domain/Money; was HomeViews.tsx's, HIERARCHY follow-up 8, BL-189): who owes what
// (receivables.aging): what's owed as a figure and how much of it is late; in the office each bucket on
// its hairline with its bar, every bar on one track so one compares with the next (late hatched, over 30
// days red); then the latest late invoices, the most overdue first (three, two in a chat), red past 30
// days. In a chat, a way into Flowology. Reads only --aging-*; Figure, Meter and Button read their own.
import { AGING_BUCKETS, type AgingProps } from '../../schemas.ts';
import type { ViewProps } from '../../host.ts';
import { money, plural } from '../../format.ts';
import { Figure } from '../Figures/Figures.tsx';
import { Meter } from '../Meter/Meter.tsx';
import { ViewAct } from '../view-act.tsx';
import './Aging.css';

const BUCKET_WORDS: Record<(typeof AGING_BUCKETS)[number], string> = {
  current: 'Not due yet',
  days_1_30: '1 to 30 days late',
  days_31_60: '31 to 60 days late',
  days_61_90: '61 to 90 days late',
  days_over_90: 'Over 90 days',
};

/** receivables.aging */
export function Aging({ data: a, host }: ViewProps<AgingProps>) {
  const chat = host.surface === 'chat';
  const late = a.rows.filter((r) => r.daysPastDue > 0).sort((x, y) => y.daysPastDue - x.daysPastDue);
  const most = Math.max(1, ...AGING_BUCKETS.map((b) => a.buckets[b]));
  const lateCents = AGING_BUCKETS.filter((b) => b !== 'current').reduce((s, b) => s + a.buckets[b], 0);
  return (
    <article className="fl-aging" data-surface={host.surface} aria-label="Who owes you">
      <Figure
        label="Owed to you"
        value={money(a.openInvoicesCents)}
        sub={lateCents ? `${money(lateCents)} late` : 'Nothing late'}
      />
      {!chat && (
        <ul className="fl-aging__rows fl-aging__buckets">
          {AGING_BUCKETS.map((b) => (
            <li key={b} className="fl-aging__row">
              <span>
                {BUCKET_WORDS[b]}
                {a.buckets[b] > 0 ? (
                  <Meter
                    value={a.buckets[b]}
                    max={most}
                    label={BUCKET_WORDS[b]}
                    fill={b === 'current' ? 'solid' : 'hatch'}
                    state={b === 'days_31_60' || b === 'days_61_90' || b === 'days_over_90' ? 'bad' : 'ok'}
                  />
                ) : null}
              </span>
              <span>
                {a.buckets[b] ? money(a.buckets[b]) : <span className="fl-aging__muted">Nothing</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {late.length > 0 && (
        <ul className="fl-aging__rows">
          {late.slice(0, chat ? 2 : 3).map((r) => (
            <li key={r.invoiceId} className="fl-aging__row">
              <span>
                <b>{r.customerName}</b>
                <span className="fl-aging__sub">
                  {r.number}, {plural(r.daysPastDue, 'day')} late{r.onHold ? ', on hold' : ''}
                </span>
              </span>
              <span className={r.daysPastDue > 30 ? 'fl-aging__bad' : undefined}>
                {money(r.balanceCents)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {chat && (
        <div className="fl-aging__acts">
          <ViewAct surface="chat" onPress={() => host.open({ kind: 'owed' })}>
            Open in Flowology
          </ViewAct>
        </div>
      )}
    </article>
  );
}
